import { useState } from 'react';
import { supabase } from '../../../../lib/supabaseClient';
import { BlogCategory, BlogPost } from '../../../../types/blog';
import CategoryFormModal from './CategoryFormModal';

interface Props {
  categories: BlogCategory[];
  posts: BlogPost[];
  onRefresh: () => void;
  onToast: (type: 'success' | 'error', message: string) => void;
}

export default function AdminCategorySection({ categories, posts, onRefresh, onToast }: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<BlogCategory | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const getPostCount = (catId: string) =>
    posts.filter((p) => p.category_id === catId).length;

  const handleAdd = () => {
    setEditCategory(null);
    setModalOpen(true);
  };

  const handleEdit = (cat: BlogCategory) => {
    setEditCategory(cat);
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    setDeleting(true);
    const { error } = await supabase
      .from('8a4f04ed_blog_categories')
      .delete()
      .eq('id', id);
    setDeleting(false);
    setConfirmId(null);
    if (error) {
      onToast('error', '削除に失敗しました。記事が紐づいている可能性があります。');
    } else {
      onToast('success', 'カテゴリーを削除しました。');
      onRefresh();
    }
  };

  const handleSaved = () => {
    onToast('success', editCategory ? 'カテゴリーを更新しました。' : 'カテゴリーを追加しました。');
    onRefresh();
  };

  return (
    <>
      <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #e8d8cc' }}>
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: '1px solid #e8d8cc' }}
        >
          <div>
            <h2 className="font-bold" style={{ color: '#4a3728', fontSize: '15px' }}>
              カテゴリー一覧
            </h2>
            <p className="text-xs mt-0.5" style={{ color: '#9b8b7f' }}>
              {categories.length}件のカテゴリー
            </p>
          </div>
          <button
            onClick={handleAdd}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
            style={{ background: '#5a9e6f' }}
          >
            <span className="flex items-center justify-center w-4 h-4">
              <i className="ri-add-line text-sm"></i>
            </span>
            カテゴリー追加
          </button>
        </div>

        {categories.length === 0 ? (
          <div className="text-center py-20" style={{ color: '#9b8b7f' }}>
            <div
              className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full"
              style={{ background: '#f2f8f4' }}
            >
              <i className="ri-folder-line text-3xl" style={{ color: '#5a9e6f' }}></i>
            </div>
            <p className="text-base font-bold mb-1" style={{ color: '#4a3728' }}>カテゴリーがありません</p>
            <p className="text-sm">「カテゴリー追加」から最初のカテゴリーを作成しましょう。</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: '#f0e8e0' }}>
            {categories.map((cat) => {
              const count = getPostCount(cat.id);
              const isConfirming = confirmId === cat.id;
              return (
                <div
                  key={cat.id}
                  className="flex items-center gap-4 px-6 py-4 hover:bg-[#fffaf6] transition-colors"
                >
                  {/* Color dot */}
                  <div
                    className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: cat.color + '22' }}
                  >
                    <div className="w-4 h-4 rounded-full" style={{ background: cat.color }}></div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm" style={{ color: '#4a3728' }}>
                        {cat.name}
                      </span>
                      <span
                        className="inline-block px-2 py-0.5 rounded-full text-xs font-bold text-white"
                        style={{ background: cat.color }}
                      >
                        {cat.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs font-mono" style={{ color: '#b0a09a' }}>
                        /{cat.slug}
                      </span>
                      <span className="text-xs" style={{ color: '#9b8b7f' }}>
                        記事 {count}件
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="shrink-0">
                    {isConfirming ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs whitespace-nowrap" style={{ color: '#cc4444' }}>
                          削除しますか？
                        </span>
                        <button
                          onClick={() => handleDelete(cat.id)}
                          disabled={deleting}
                          className="px-2 py-1 rounded text-xs font-bold text-white whitespace-nowrap cursor-pointer disabled:opacity-60"
                          style={{ background: '#cc4444' }}
                        >
                          はい
                        </button>
                        <button
                          onClick={() => setConfirmId(null)}
                          className="px-2 py-1 rounded text-xs font-bold whitespace-nowrap cursor-pointer"
                          style={{ background: '#f0e8e0', color: '#6b5a4e' }}
                        >
                          いいえ
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(cat)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap"
                          style={{ background: '#f2f8f4', color: '#5a9e6f', border: '1px solid #c8e0cf' }}
                        >
                          <span className="flex items-center justify-center w-3 h-3">
                            <i className="ri-edit-line text-xs"></i>
                          </span>
                          編集
                        </button>
                        <button
                          onClick={() => setConfirmId(cat.id)}
                          disabled={count > 0}
                          title={count > 0 ? `${count}件の記事が紐づいているため削除できません` : '削除'}
                          className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ background: '#fff0f0', color: '#cc4444', border: '1px solid #f8c4c4' }}
                        >
                          <span className="flex items-center justify-center w-3 h-3">
                            <i className="ri-delete-bin-line text-xs"></i>
                          </span>
                          削除
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CategoryFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        category={editCategory}
        onSaved={handleSaved}
      />
    </>
  );
}
